import { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useUnsavedChanges } from "hooks/useUnsavedChanges";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import Fab from "@mui/material/Fab";
import Collapse from "@mui/material/Collapse";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import SkeletonTable from "components/SkeletonTable";
import { useAuthz } from "context/AuthzContext";
import { useClient } from "context/ClientContext";
import { PERMISSIONS } from "auth/permissions";
import { TC } from "../tokens/text";
import {
  listClientProfiles,
  createClientProfile,
  createClientProfileFromCH,
  updateClientProfile,
  archiveClientProfile,
  listClientProfileConsents,
  listTenantUsers,
  lookupCompaniesHouse,
  resyncClientFromCH,
  listDirectors,
  listAllDirectorsForTenant,
  provisionDirectorProfiles,
} from "services/tenantUserAdminService";

// ─── Constants ────────────────────────────────────────────────────────────────

const BUSINESS_TYPES = [
  { value: "ltd",         label: "Limited Company (Ltd)" },
  { value: "llp",         label: "LLP" },
  { value: "sole_trader", label: "Sole Trader" },
  { value: "partnership", label: "Partnership" },
  { value: "other",       label: "Other" },
];

const JURISDICTIONS = [
  { value: "UK",    label: "United Kingdom" },
  { value: "IE",    label: "Ireland" },
  { value: "DE",    label: "Germany" },
  { value: "FR",    label: "France" },
  { value: "NL",    label: "Netherlands" },
  { value: "other", label: "Other" },
];

const CURRENCIES = [
  { value: "GBP", label: "GBP £" },
  { value: "EUR", label: "EUR €" },
  { value: "USD", label: "USD $" },
];

const VAT_SCHEMES = [
  { value: "none",      label: "No VAT" },
  { value: "cash",      label: "Cash Accounting" },
  { value: "standard",  label: "Accruals" },
  { value: "flat_rate", label: "Flat Rate" },
];

const TAX_REGIMES = [
  { value: "uk_vat", label: "UK VAT" },
  { value: "eu_vat", label: "EU VAT" },
  { value: "mixed",  label: "Mixed (UK + EU)" },
  { value: "none",   label: "None" },
];

const ACCOUNTING_BASIS = [
  { value: "accrual", label: "Accrual Basis" },
  { value: "cash",    label: "Cash Basis" },
];

const ONBOARDING_STATUS = [
  { value: "pending",  label: "Pending" },
  { value: "partial",  label: "Partial" },
  { value: "complete", label: "Complete" },
];

const ACCESS_LEVELS = [
  { value: "partner",   label: "Partner" },
  { value: "senior",    label: "Senior Accountant" },
  { value: "reviewer",  label: "Reviewer" },
  { value: "read_only", label: "Read Only" },
];

const EMPTY_FORM = {
  // legal_identity
  legalName: "", tradingName: "", companyRegNumber: "", businessType: "ltd",
  jurisdiction: "UK", registeredAddress: "", incorporationDate: "", companyNotes: "",
  // tax_identity
  vatRegistered: false, vatNumber: "", vatScheme: "none", taxJurisdiction: "UK",
  utrNumber: "", corporationTaxNumber: "", payeReference: "",
  // financial_context
  baseCurrency: "GBP", financialYearStart: "04-06",
  chartOfAccountsTemplateId: "", accountingBasis: "accrual",
  // contact
  contactName: "", contactEmail: "",
  // relationship
  assignedAccountantUid: "", accessLevel: "senior",
  // status
  onboardingStatus: "pending",
  // companies_house_data (auto-populated from CH, saved alongside profile)
  companyStatus: "", companyTypeRaw: "", sicCodes: [],
  accountingReferenceDate: null,
  nextAccountsDue: null, nextAccountsPeriodEndOn: null,
  lastAccountsMadeUpTo: null, lastAccountsType: null,
  confirmationStatementNextDue: null, confirmationStatementLastMadeUpTo: null,
  hasCharges: false, hasInsolvencyHistory: false, hasBeenLiquidated: false,
  canFile: false,
  registeredAddressObj: {
    addressLine1: "", addressLine2: "", locality: "",
    region: "", postalCode: "", country: "",
  },
};

const DIALOG_PAPER_SX = {
  backgroundColor: "#ffffff",
  color: TC.primary,
  border: "1px solid #d8e6f7",
  borderRadius: "16px",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function labelFor(list, value) {
  return list.find((i) => i.value === value)?.label || value || "—";
}

function StatusBadge({ status }) {
  const cfg = {
    active:    { content: "Active",    color: "success" },
    suspended: { content: "Suspended", color: "warning" },
    archived:  { content: "Archived",  color: "error"   },
    dissolved: { content: "Dissolved", color: "error"   },
  }[status] || { content: status, color: "default" };

  return (
    <VuiBadge
      variant="standard"
      badgeContent={cfg.content}
      color={cfg.color}
      size="xs"
      container
      sx={({ palette, borders: { borderRadius, borderWidth } }) => ({
        background: cfg.color === "success" ? palette.success.main
          : cfg.color === "warning" ? "rgba(245,166,35,0.85)"
          : cfg.color === "error"   ? palette.error.main
          : "rgba(255,255,255,0.12)",
        border: `${borderWidth[1]} solid ${
          cfg.color === "success" ? palette.success.main
          : cfg.color === "warning" ? "rgba(245,166,35,0.85)"
          : cfg.color === "error"   ? palette.error.main
          : "rgba(255,255,255,0.2)"
        }`,
        borderRadius: borderRadius.md,
        color: palette.white.main,
      })}
    />
  );
}

function StyledSelect({ value, onChange, children, placeholder }) {
  return (
    <FormControl fullWidth size="small">
      <Select
        value={value}
        onChange={onChange}
        displayEmpty
        sx={{
          color: TC.primary, fontSize: "0.875rem",
          background: "#f4f8ff",
          border: "1px solid #d8e6f7",
          borderRadius: "8px",
          "& .MuiSelect-icon": { color: TC.secondary },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&:hover": { background: "#f4f8ff" },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              background: "rgba(15,18,59,0.98)",
              border: "1px solid #d8e6f7",
              color: TC.primary,
              "& .MuiMenuItem-root:hover": { background: "rgba(0,117,255,0.15)" },
              "& .MuiMenuItem-root.Mui-selected": { background: "rgba(0,117,255,0.25)" },
            },
          },
        }}
      >
        {placeholder && (
          <MenuItem value="" disabled sx={{ color: TC.secondary, fontSize: "0.875rem" }}>
            {placeholder}
          </MenuItem>
        )}
        {children}
      </Select>
    </FormControl>
  );
}

function SectionLabel({ children }) {
  return (
    <VuiTypography
      variant="caption" fontWeight="bold"
      sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", mt: 1, display: "block" }}
    >
      {children}
    </VuiTypography>
  );
}

// ─── View Detail Dialog ───────────────────────────────────────────────────────

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <Grid item xs={12} sm={6}>
      <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  mb: 0.2 }}>
        {label}
      </VuiTypography>
      <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>
        {value}
      </VuiTypography>
    </Grid>
  );
}

function ViewDetailDialog({ open, onClose, profile, tenantId, userMap = {} }) {
  const history = useHistory();
  const [consents, setConsents] = useState([]);
  const [consentsLoading, setConsentsLoading] = useState(false);
  const [directors, setDirectors] = useState([]);
  const [directorsLoading, setDirectorsLoading] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState("");

  useEffect(() => {
    if (!open || !profile || !tenantId) return;
    setConsentsLoading(true);
    listClientProfileConsents(tenantId, profile.id)
      .then(({ consents: c }) => setConsents(c))
      .catch(() => setConsents([]))
      .finally(() => setConsentsLoading(false));

    setDirectorsLoading(true);
    listDirectors(tenantId, profile.id)
      .then(({ directors: d }) => setDirectors(d))
      .catch(() => setDirectors([]))
      .finally(() => setDirectorsLoading(false));
  }, [open, profile, tenantId]);

  const handleResync = async () => {
    setResyncing(true);
    setResyncMsg("");
    try {
      const result = await resyncClientFromCH(tenantId, profile.id);
      const changes = Array.isArray(result?.changes) ? result.changes : [];
      const dr = result?.directors || {};
      setResyncMsg(
        `Resync complete. ${changes.length} field change(s), ${dr.created || 0} new director(s), ${dr.updated || 0} updated.`
      );
      // Refresh directors list
      listDirectors(tenantId, profile.id)
        .then((res) => setDirectors(res?.directors ?? []))
        .catch(() => {});
    } catch (err) {
      setResyncMsg(err?.message || "Resync failed.");
    } finally {
      setResyncing(false);
    }
  };

  if (!profile) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="client-profile-dialog-title" PaperProps={{ sx: DIALOG_PAPER_SX }}>
      <DialogTitle id="client-profile-dialog-title" sx={{ color: TC.primary, pb: 0.5 }}>
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <BusinessIcon sx={{ color: TC.linkHover }} />
          <VuiBox>
            <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>{profile.legalName}</VuiTypography>
            <VuiBox display="flex" gap={1} mt={0.3}>
              <StatusBadge status={profile.status} />
              <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{labelFor(JURISDICTIONS, profile.jurisdiction)}</VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </DialogTitle>

      <DialogContent dividers sx={{ bordercolor: "#7a94b0" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}><SectionLabel>Company Profile</SectionLabel></Grid>
          <DetailRow label="Legal Name"             value={profile.legalName} />
          <DetailRow label="Trading Name"           value={profile.tradingName} />
          <DetailRow label="Company Reg Number"     value={profile.companyRegNumber} />
          <DetailRow label="Nature of Business"       value={profile.sicCodes?.length > 0 ? profile.sicCodes.join(", ") : null} />
          <DetailRow label="Jurisdiction / Country" value={labelFor(JURISDICTIONS, profile.jurisdiction)} />
          <DetailRow label="Incorporation Date"     value={profile.incorporationDate} />
          <DetailRow label="Registered Address"     value={profile.registeredAddress} />

          <Grid item xs={12}><Divider sx={{ bordercolor: "#7a94b0" }} /></Grid>
          <Grid item xs={12}><SectionLabel>Tax Identity</SectionLabel></Grid>
          <DetailRow label="VAT Registered"         value={profile.vatRegistered ? "Yes" : "No"} />
          <DetailRow label="VAT Number"             value={profile.vatNumber} />
          <DetailRow label="VAT Scheme"             value={labelFor(VAT_SCHEMES, profile.vatScheme)} />
          <DetailRow label="UTR Number"             value={profile.utrNumber} />
          <DetailRow label="Corporation Tax No"     value={profile.corporationTaxNumber} />
          <DetailRow label="PAYE Reference"         value={profile.payeReference} />

          <Grid item xs={12}><Divider sx={{ bordercolor: "#7a94b0" }} /></Grid>
          <Grid item xs={12}><SectionLabel>Client Contact</SectionLabel></Grid>
          <DetailRow label="Contact Name"           value={profile.contactName} />
          <DetailRow label="Contact Email"          value={profile.contactEmail} />

          <Grid item xs={12}><Divider sx={{ bordercolor: "#7a94b0" }} /></Grid>
          <Grid item xs={12}><SectionLabel>Financial Context</SectionLabel></Grid>
          <DetailRow label="Base Currency"          value={labelFor(CURRENCIES, profile.baseCurrency)} />
          <DetailRow label="Financial Year Start"   value={profile.financialYearStart} />
          <DetailRow label="Accounting Basis"       value={labelFor(ACCOUNTING_BASIS, profile.accountingBasis)} />
          <DetailRow label="VAT Scheme"             value={labelFor(VAT_SCHEMES, profile.vatScheme)} />
          <DetailRow label="Chart of Accounts"      value={profile.chartOfAccountsTemplateId || "Default"} />

          <Grid item xs={12}><Divider sx={{ bordercolor: "#7a94b0" }} /></Grid>
          <Grid item xs={12}><SectionLabel>Accountant Relationship</SectionLabel></Grid>
          <DetailRow label="Access Level"            value={labelFor(ACCESS_LEVELS, profile.accessLevel)} />
          <DetailRow label="Assigned Accountant UID" value={profile.assignedAccountantUid} />
          <DetailRow label="Onboarding Status"       value={labelFor(ONBOARDING_STATUS, profile.onboardingStatus)} />

          {/* ── Companies House Data ──────────────────────────────────── */}
          {(profile.companyStatus || profile.sicCodes?.length > 0 || profile.nextAccountsDue || profile.chLastSyncedAt) && (
            <>
              <Grid item xs={12}><Divider sx={{ bordercolor: "#7a94b0" }} /></Grid>
              <Grid item xs={12}>
                <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <VuiBox display="flex" alignItems="center" gap={1}>
                    <VuiTypography variant="caption" fontWeight="bold"
                      sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      🏛 Companies House Data
                    </VuiTypography>
                    {profile.companyStatus && (
                      <Chip
                        label={profile.companyStatus.toUpperCase()}
                        size="small"
                        sx={{
                          height: 18, fontSize: "0.6rem", fontWeight: 700,
                          bgcolor: profile.companyStatus === "active" ? "rgba(76,175,80,0.2)" : "rgba(244,67,54,0.2)",
                          color:   profile.companyStatus === "active" ? "#4caf50" : "#f44336",
                          border:  `1px solid ${profile.companyStatus === "active" ? "#4caf50" : "#f44336"}`,
                        }}
                      />
                    )}
                  </VuiBox>
                  {profile.chLastSyncedAt && (
                    <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.65rem", opacity: 0.6 }}>
                      Last synced {new Date(profile.chLastSyncedAt).toLocaleString("en-GB")}
                    </VuiTypography>
                  )}
                </VuiBox>

                {/* Identity */}
                <VuiBox mb={1.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                  <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1 }}>
                    🏢 Company Identity
                  </VuiTypography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: "CH Company Type", value: profile.companyTypeRaw },
                      { label: "Accounting Ref",  value: profile.accountingReferenceDate },
                    ].filter(f => f.value).map(f => (
                      <Grid item xs={6} sm={4} key={f.label}>
                        <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  fontSize: "0.62rem", mb: 0.2, opacity: 0.7 }}>{f.label}</VuiTypography>
                        <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem" }}>{f.value}</VuiTypography>
                      </Grid>
                    ))}
                  </Grid>
                </VuiBox>

                {/* Registered address */}
                {profile.registeredAddressObj && Object.values(profile.registeredAddressObj).some(Boolean) && (
                  <VuiBox mb={1.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                    <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1 }}>
                      📍 Registered Address (CH)
                    </VuiTypography>
                    <Grid container spacing={1.5}>
                      {[
                        { label: "Line 1",      value: profile.registeredAddressObj.addressLine1 },
                        { label: "Line 2",      value: profile.registeredAddressObj.addressLine2 },
                        { label: "Town / City", value: profile.registeredAddressObj.locality },
                        { label: "Region",      value: profile.registeredAddressObj.region },
                        { label: "Postcode",    value: profile.registeredAddressObj.postalCode },
                        { label: "Country",     value: profile.registeredAddressObj.country },
                      ].filter(f => f.value).map(f => (
                        <Grid item xs={6} sm={4} key={f.label}>
                          <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  fontSize: "0.62rem", mb: 0.2, opacity: 0.7 }}>{f.label}</VuiTypography>
                          <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem" }}>{f.value}</VuiTypography>
                        </Grid>
                      ))}
                    </Grid>
                  </VuiBox>
                )}

                {/* Filing dates */}
                <VuiBox mb={1.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                  <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1 }}>
                    📅 Accounts &amp; Filing Dates
                  </VuiTypography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: "Next Accounts Due",      value: profile.nextAccountsDue },
                      { label: "Accounts Period End",    value: profile.nextAccountsPeriodEndOn },
                      { label: "Last Accounts Made Up",  value: profile.lastAccountsMadeUpTo },
                      { label: "Last Accounts Type",     value: profile.lastAccountsType },
                      { label: "Conf. Statement Due",    value: profile.confirmationStatementNextDue },
                      { label: "Last Conf. Statement",   value: profile.confirmationStatementLastMadeUpTo },
                    ].filter(f => f.value).map(f => (
                      <Grid item xs={6} sm={4} key={f.label}>
                        <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  fontSize: "0.62rem", mb: 0.2, opacity: 0.7 }}>{f.label}</VuiTypography>
                        <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem" }}>{f.value}</VuiTypography>
                      </Grid>
                    ))}
                  </Grid>
                </VuiBox>

                {/* SIC codes */}
                {profile.sicCodes?.length > 0 && (
                  <VuiBox mb={1.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                    <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1 }}>
                      🏭 SIC Codes
                    </VuiTypography>
                    <VuiBox display="flex" gap={0.8} flexWrap="wrap">
                      {profile.sicCodes.map(code => (
                        <Chip key={code} label={code} size="small"
                          sx={{ bgcolor: "rgba(0,117,255,0.15)", color: TC.link, border: "1px solid rgba(0,117,255,0.3)", fontSize: "0.72rem", height: 22 }} />
                      ))}
                    </VuiBox>
                  </VuiBox>
                )}

                {/* Risk flags */}
                {(profile.hasCharges || profile.hasInsolvencyHistory || profile.hasBeenLiquidated) && (
                  <VuiBox mb={1.5} p={1.5} sx={{ background: "rgba(244,67,54,0.08)", borderRadius: "8px", border: "1px solid rgba(244,67,54,0.35)" }}>
                    <VuiTypography variant="caption" fontWeight="bold" sx={{ textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.72rem", display: "block", mb: 1, color: "#f44336" }}>
                      ⚠ Risk &amp; Compliance Flags
                    </VuiTypography>
                    <VuiBox display="flex" gap={1} flexWrap="wrap">
                      {profile.hasCharges          && <Chip label="⚠ Has Charges"          size="small" sx={{ bgcolor: "rgba(255,152,0,0.25)",  color: "#ffb74d", border: "1px solid rgba(255,152,0,0.6)",  fontSize: "0.8rem", fontWeight: 600 }} />}
                      {profile.hasInsolvencyHistory && <Chip label="🚨 Insolvency History"  size="small" sx={{ bgcolor: "rgba(244,67,54,0.25)", color: "#ef5350", border: "1px solid rgba(244,67,54,0.6)", fontSize: "0.8rem", fontWeight: 600 }} />}
                      {profile.hasBeenLiquidated   && <Chip label="🚨 Has Been Liquidated"  size="small" sx={{ bgcolor: "rgba(244,67,54,0.25)", color: "#ef5350", border: "1px solid rgba(244,67,54,0.6)", fontSize: "0.8rem", fontWeight: 600 }} />}
                    </VuiBox>
                  </VuiBox>
                )}
              </Grid>
            </>
          )}

          {/* ── Linked Bank Consents ──────────────────────────────────── */}
          <Grid item xs={12}><Divider sx={{ bordercolor: "#7a94b0" }} /></Grid>
          <Grid item xs={12}>
            <VuiTypography
              variant="caption" fontWeight="bold"
              sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", display: "block" }}
            >
              Bank Consents
            </VuiTypography>
            <VuiTypography variant="caption" display="block" mt={0.3} mb={1} sx={{ color: TC.secondary }}>
              All bank consents owned by this client profile.
            </VuiTypography>

            {consentsLoading ? (
              <LoadingSpinner size="sm" label="Loading consents…" centered={false} />
            ) : consents.length === 0 ? (
              <VuiBox px={2} py={1.5} borderRadius="8px"
                sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7" }}>
                <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                  No bank consents yet. Request one from the Bank Feeds page.
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox
                sx={{
                  "& th": { borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}` },
                  "& .MuiTableRow-root:not(:last-child) td": {
                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}`,
                  },
                }}
              >
                <Table
                  columns={[
                    { name: "bank",    align: "left"   },
                    { name: "status",  align: "center" },
                    { name: "granted", align: "left"   },
                    { name: "expires", align: "left"   },
                  ]}
                  rows={consents.map((c) => ({
                    hasBorder: true,
                    bank: (
                      <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>
                        {c.bank || "—"}
                      </VuiTypography>
                    ),
                    status: (
                      <VuiBadge
                        variant="standard"
                        badgeContent={c.status}
                        color={c.status === "active" ? "success" : c.status === "pending" ? "info" : "error"}
                        size="xs"
                        container
                        sx={({ palette, borders: { borderRadius, borderWidth } }) => ({
                          background: c.status === "active" ? palette.success.main
                            : c.status === "pending" ? palette.info.main
                            : palette.error.main,
                          border: `${borderWidth[1]} solid transparent`,
                          borderRadius: borderRadius.md,
                          color: palette.white.main,
                        })}
                      />
                    ),
                    granted: (
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </VuiTypography>
                    ),
                    expires: (
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                        {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </VuiTypography>
                    ),
                  }))}
                />
              </VuiBox>
            )}
          </Grid>

          {/* ── Directors ─────────────────────────────────────────── */}
          <Grid item xs={12}><Divider sx={{ bordercolor: "#7a94b0" }} /></Grid>
          <Grid item xs={12}>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <VuiTypography
                variant="caption" fontWeight="bold"
                sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em" }}
              >
                Directors &amp; Officers
              </VuiTypography>
              <VuiBox display="flex" gap={1}>
                <VuiButton
                  color="text"
                  size="small"
                  variant="outlined"
                  onClick={() => { onClose(); history.push(`/directors?clientId=${profile.id}`); }}
                  sx={{ fontSize: "0.7rem", py: 0.4, px: 1.2 }}
                >
                  👥 View All Directors
                </VuiButton>
                {profile.companyRegNumber && (
                  <VuiButton
                    color="info"
                    size="small"
                    variant="outlined"
                    onClick={handleResync}
                    disabled={resyncing}
                    sx={{ fontSize: "0.7rem", py: 0.4, px: 1.2 }}
                  >
                    {resyncing ? "Resyncing…" : "↻ Resync from CH"}
                  </VuiButton>
                )}
              </VuiBox>
            </VuiBox>
            {resyncMsg && (
              <VuiTypography variant="caption" sx={{ color: resyncMsg.includes("failed") ? "#f44336" : "#4caf50" }} display="block" mb={1}>
                {resyncMsg}
              </VuiTypography>
            )}
            {directorsLoading ? (
              <LoadingSpinner size="sm" label="Loading directors…" centered={false} />
            ) : directors.length === 0 ? (
              <VuiBox px={2} py={1.5} borderRadius="8px"
                sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7" }}>
                <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                  No directors found. {profile.companyRegNumber ? `Use "Resync from CH" to import from Companies House.` : "Add a company reg number and resync."}
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox sx={{
                "& th": { borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}` },
                "& .MuiTableRow-root:not(:last-child) td": {
                  borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}`,
                },
              }}>
                <Table
                  columns={[
                    { name: "name",       align: "left"   },
                    { name: "role",       align: "left"   },
                    { name: "appointed",  align: "left"   },
                    { name: "dob",        align: "left"   },
                    { name: "nationality",align: "left"   },
                    { name: "status",     align: "center" },
                  ]}
                  rows={directors.map((d) => ({
                    hasBorder: true,
                    name: (
                      <VuiBox>
                        <VuiTypography variant="caption" color={d.resignedOn ? "text" : "white"} fontWeight="medium" display="block">
                          {d.fullName}
                        </VuiTypography>
                        {d.occupation && (
                          <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.65rem", opacity: 0.7 }}>
                            {d.occupation}
                          </VuiTypography>
                        )}
                        {d.serviceAddress?.postalCode && (
                          <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.6rem", opacity: 0.5 }}>
                            {[d.serviceAddress.locality, d.serviceAddress.postalCode].filter(Boolean).join(", ")}
                          </VuiTypography>
                        )}
                      </VuiBox>
                    ),
                    role: (
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                        {d.role?.replace(/_/g, " ") || "Director"}
                      </VuiTypography>
                    ),
                    appointed: (
                      <VuiBox>
                        <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>{d.appointedOn || "—"}</VuiTypography>
                        {d.resignedOn && (
                          <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.65rem", opacity: 0.7 }}>
                            Resigned {d.resignedOn}
                          </VuiTypography>
                        )}
                      </VuiBox>
                    ),
                    dob: (
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                        {d.dateOfBirth || "—"}
                      </VuiTypography>
                    ),
                    nationality: (
                      <VuiBox>
                        <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>{d.nationality || "—"}</VuiTypography>
                        {d.countryOfResidence && d.countryOfResidence !== d.nationality && (
                          <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.65rem", opacity: 0.6 }}>
                            Res: {d.countryOfResidence}
                          </VuiTypography>
                        )}
                      </VuiBox>
                    ),
                    status: d.resignedOn ? (
                      <VuiBadge variant="standard" badgeContent="Resigned" color="error" size="xs" container
                        sx={({ palette, borders: { borderRadius, borderWidth } }) => ({
                          background: palette.error.main, border: `${borderWidth[1]} solid transparent`,
                          borderRadius: borderRadius.md, color: palette.white.main,
                        })} />
                    ) : (
                      <VuiBadge variant="standard" badgeContent="Active" color="success" size="xs" container
                        sx={({ palette, borders: { borderWidth, borderRadius } }) => ({
                          background: palette.success.main, border: `${borderWidth[1]} solid transparent`,
                          borderRadius: borderRadius.md, color: palette.white.main,
                        })} />
                    ),
                  }))}
                />
              </VuiBox>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <VuiButton color="info" size="small" onClick={onClose}>Close</VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Profile Form Dialog ──────────────────────────────────────────────────────

function ProfileFormDialog({ open, onClose, onSaved, tenantId, existingProfile, initialClientType = "company" }) {
  const isEdit = Boolean(existingProfile);
  // Determine whether we're creating/editing a company or individual
  const clientType = existingProfile?.clientType || initialClientType;
  const isIndividual = clientType === "individual";

  const [form, setForm] = useState(EMPTY_FORM);
  // For individual forms: first name and surname as separate inputs
  const [indFirstName, setIndFirstName] = useState("");
  const [indSurname,   setIndSurname]   = useState("");
  const [indEmail,     setIndEmail]     = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [chLookupNumber, setChLookupNumber] = useState("");
  const [chLooking, setChLooking] = useState(false);
  const [chLookupError, setChLookupError] = useState("");
  const [chPopulated, setChPopulated] = useState(false);
  const [chData, setChData] = useState(null);

  // Dirty tracking — set true on any user input; reset when dialog opens/closes
  const [hasModified, setHasModified] = useState(false);
  const isDirty = open && hasModified;
  const { guardClose, ConfirmDialog } = useUnsavedChanges(isDirty);

  useEffect(() => {
    if (open) {
      const freshForm = existingProfile ? { ...EMPTY_FORM, ...existingProfile } : EMPTY_FORM;
      setForm(freshForm);
      let freshInd = "||";
      if (isIndividual && existingProfile) {
        const pp = existingProfile.personalProfile || {};
        const fullName = pp.full_legal_name || existingProfile.legalName || "";
        const parts = fullName.split(" ");
        const sn = parts.length > 1 ? parts[parts.length - 1] : fullName;
        const fn = parts.length > 1 ? parts.slice(0, -1).join(" ") : "";
        setIndSurname(sn); setIndFirstName(fn); setIndEmail(existingProfile.contactEmail || "");
        freshInd = `${fn}|${sn}|${existingProfile.contactEmail || ""}`;
      } else {
        setIndFirstName(""); setIndSurname(""); setIndEmail("");
      }
      setHasModified(false);
      setError("");
      setChLookupNumber("");
      setChLookupError("");
      setChPopulated(false);
      setChData(null);
    } else {
      setHasModified(false);
    }
  }, [open, existingProfile]); // eslint-disable-line

  const set = (key) => (e) => { setHasModified(true); setForm((p) => ({ ...p, [key]: e?.target?.value ?? e })); };
  const setAddr = (key) => (e) => { setHasModified(true); setForm((p) => ({
    ...p,
    registeredAddressObj: { ...(p.registeredAddressObj || {}), [key]: e.target.value },
  })); };

  const handleChLookup = async () => {
    if (!chLookupNumber.trim()) return;
    setChLooking(true);
    setChLookupError("");
    setChPopulated(false);
    setChData(null);
    try {
      const result = await lookupCompaniesHouse(chLookupNumber.trim());
      const { company, directors: fetchedDirs } = result;
      setChData({ company, directors: fetchedDirs });
      setForm((prev) => ({
        ...prev,
        // Core identity
        legalName:                  company.legalName                  || prev.legalName,
        companyRegNumber:           company.companyRegNumber           || prev.companyRegNumber,
        businessType:               company.businessType               || prev.businessType,
        jurisdiction:               company.jurisdiction               || prev.jurisdiction,
        registeredAddress:          company.registeredAddress          || prev.registeredAddress,
        incorporationDate:          company.incorporationDate          || prev.incorporationDate,
        // Financial year start auto-derived from accounting reference date
        financialYearStart:         company.accountingReferenceDate    || prev.financialYearStart,
        // All CH-specific fields
        companyStatus:              company.companyStatus              || "",
        companyTypeRaw:             company.companyTypeRaw             || "",
        sicCodes:                   company.sicCodes                   || [],
        accountingReferenceDate:    company.accountingReferenceDate    || null,
        nextAccountsDue:            company.nextAccountsDue            || null,
        nextAccountsPeriodEndOn:    company.nextAccountsPeriodEndOn    || null,
        lastAccountsMadeUpTo:       company.lastAccountsMadeUpTo       || null,
        lastAccountsType:           company.lastAccountsType           || null,
        confirmationStatementNextDue:      company.confirmationStatementNextDue      || null,
        confirmationStatementLastMadeUpTo: company.confirmationStatementLastMadeUpTo || null,
        hasCharges:                 company.hasCharges                 || false,
        hasInsolvencyHistory:       company.hasInsolvencyHistory       || false,
        hasBeenLiquidated:          company.hasBeenLiquidated          || false,
        canFile:                    company.canFile                    || false,
        registeredAddressObj: company.registeredAddressObj || {
          addressLine1: "", addressLine2: "", locality: "",
          region: "", postalCode: "", country: "",
        },
      }));
      setChPopulated(true);
    } catch (err) {
      setChLookupError(err?.message || "Company not found at Companies House.");
    } finally {
      setChLooking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isIndividual) {
      if (!indSurname.trim() && !indFirstName.trim()) { setError("At least a surname or first name is required."); return; }
      setIsSubmitting(true);
      setError("");
      try {
        const fullName = [indFirstName.trim(), indSurname.trim()].filter(Boolean).join(" ");
        const payload = {
          clientType:   "individual",
          businessType: "individual",
          legalName:    fullName,
          contactEmail: indEmail.trim(),
          personalProfile: {
            full_legal_name: fullName,
            preferred_name:  "",
          },
        };
        const saved = isEdit
          ? await updateClientProfile(tenantId, existingProfile.id, payload)
          : await createClientProfile(tenantId, payload);
        onSaved(saved);
      } catch (err) {
        setError(err?.message || "Failed to save individual profile.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!form.legalName.trim()) { setError("Legal name is required."); return; }
    if (!form.jurisdiction)     { setError("Jurisdiction is required."); return; }
    setIsSubmitting(true);
    setError("");
    try {
      if (isEdit) {
        const saved = await updateClientProfile(tenantId, existingProfile.id, form);
        const prevReg = (existingProfile.companyRegNumber || "").trim();
        const newReg  = (form.companyRegNumber || "").trim();
        if (newReg && newReg !== prevReg) {
          try { await resyncClientFromCH(tenantId, saved.id || existingProfile.id); } catch (_) { /* silent */ }
        }
        onSaved(saved);
      } else {
        const saved = await createClientProfile(tenantId, form);
        if (form.companyRegNumber?.trim()) {
          try { await resyncClientFromCH(tenantId, saved.id); } catch (_) { /* silent */ }
        }
        onSaved(saved);
      }
    } catch (err) {
      setError(err?.message || "Failed to save client profile.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={() => !isSubmitting && guardClose(onClose)} maxWidth="md" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
      <DialogTitle sx={{ color: TC.primary, pb: 1 }}>
        {isEdit
          ? (isIndividual ? "Edit Individual" : "Edit Client Profile")
          : (isIndividual ? "👤 New Individual Client" : "🏢 New Company Profile")}
      </DialogTitle>
      <DialogContent dividers sx={{ bordercolor: "#7a94b0" }}>
        {error && <VuiTypography color="error" variant="button" mb={2} display="block">{error}</VuiTypography>}

        {/* ── Individual form ──────────────────────────────────────────── */}
        {isIndividual ? (
          <VuiBox component="form" id="client-profile-form" onSubmit={handleSubmit}>
            <SectionLabel>Personal Details</SectionLabel>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={6}>
                <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>Surname *</VuiTypography>
                <VuiInput
                  placeholder="Surname"
                  value={indSurname}
                  onChange={(e) => { setHasModified(true); setIndSurname(e.target.value); }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>First Name</VuiTypography>
                <VuiInput
                  placeholder="First name"
                  value={indFirstName}
                  onChange={(e) => { setHasModified(true); setIndFirstName(e.target.value); }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>Email</VuiTypography>
                <VuiInput
                  placeholder="Email address"
                  value={indEmail}
                  onChange={(e) => { setHasModified(true); setIndEmail(e.target.value); }}
                />
              </Grid>
            </Grid>
          </VuiBox>
        ) : (
          <VuiBox
            mb={2} p={2}
            sx={{ background: "rgba(0,117,255,0.07)", border: "1px solid rgba(0,117,255,0.25)", borderRadius: "10px" }}
          >
            <VuiTypography variant="caption" fontWeight="bold" display="block" mb={1} sx={{ color: TC.primary }}>
              🏢 Auto-populate from Companies House
            </VuiTypography>
            <VuiBox display="flex" gap={1} alignItems="center">
              <VuiInput
                placeholder="Enter Company Registration Number (e.g. 12345678)"
                value={chLookupNumber}
                onChange={(e) => { setHasModified(true); setChLookupNumber(e.target.value); }}
                sx={{ flex: 1 }}
                onKeyDown={(e) => e.key === "Enter" && handleChLookup()}
              />
              <VuiButton
                color="info"
                size="small"
                onClick={handleChLookup}
                disabled={chLooking || !chLookupNumber.trim()}
                sx={{ whiteSpace: "nowrap", minWidth: 110 }}
              >
                {chLooking ? "Looking up…" : "Lookup CH"}
              </VuiButton>
            </VuiBox>
            {chLookupError && (
              <VuiTypography variant="caption" color="error" display="block" mt={0.8}>
                {chLookupError}
              </VuiTypography>
            )}
            {chPopulated && chData && (
              <VuiBox mt={1.5} sx={{ borderTop: "1px solid rgba(0,117,255,0.2)", pt: 1.5 }}>
                {/* ── Header ── */}
                <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <VuiBox display="flex" alignItems="center" gap={1}>
                    <VuiTypography variant="caption" sx={{ color: "#4caf50", fontSize: "0.8rem" }} fontWeight="bold">
                      ✓ Companies House data loaded — all fields will be saved
                    </VuiTypography>
                    {chData.company.companyStatus && (
                      <Chip
                        label={chData.company.companyStatus.toUpperCase()}
                        size="small"
                        sx={{
                          height: 18, fontSize: "0.6rem", fontWeight: 700,
                          bgcolor: chData.company.companyStatus === "active" ? "rgba(76,175,80,0.2)" : "rgba(244,67,54,0.2)",
                          color:   chData.company.companyStatus === "active" ? "#4caf50" : "#f44336",
                          border:  `1px solid ${chData.company.companyStatus === "active" ? "#4caf50" : "#f44336"}`,
                        }}
                      />
                    )}
                  </VuiBox>
                </VuiBox>

                {/* ── Section 1: Company Identity ── */}
                <VuiBox mb={1.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                  <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1 }}>
                    🏢 Company Identity
                  </VuiTypography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: "Legal Name",       value: chData.company.legalName },
                      { label: "Reg. Number",      value: chData.company.companyRegNumber },
                      { label: "CH Type",          value: chData.company.companyTypeRaw },
                      { label: "Entity Type",      value: chData.company.businessType },
                      { label: "Incorporated",     value: chData.company.incorporationDate },
                      { label: "Jurisdiction",     value: chData.company.jurisdiction },
                    ].filter(f => f.value).map(f => (
                      <Grid item xs={6} sm={4} key={f.label}>
                        <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  fontSize: "0.62rem", mb: 0.2, opacity: 0.7 }}>{f.label}</VuiTypography>
                        <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem", fontWeight: 600 }}>{f.value}</VuiTypography>
                      </Grid>
                    ))}
                  </Grid>
                </VuiBox>

                {/* ── Section 2: Registered Address ── */}
                {chData.company.registeredAddressObj && Object.values(chData.company.registeredAddressObj).some(Boolean) && (
                  <VuiBox mb={1.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                    <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1 }}>
                      📍 Registered Address
                    </VuiTypography>
                    <Grid container spacing={1.5}>
                      {[
                        { label: "Line 1",      value: chData.company.registeredAddressObj.addressLine1 },
                        { label: "Line 2",      value: chData.company.registeredAddressObj.addressLine2 },
                        { label: "Town / City", value: chData.company.registeredAddressObj.locality },
                        { label: "Region",      value: chData.company.registeredAddressObj.region },
                        { label: "Postcode",    value: chData.company.registeredAddressObj.postalCode },
                        { label: "Country",     value: chData.company.registeredAddressObj.country },
                      ].filter(f => f.value).map(f => (
                        <Grid item xs={6} sm={4} key={f.label}>
                          <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  fontSize: "0.62rem", mb: 0.2, opacity: 0.7 }}>{f.label}</VuiTypography>
                          <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem" }}>{f.value}</VuiTypography>
                        </Grid>
                      ))}
                    </Grid>
                  </VuiBox>
                )}

                {/* ── Section 3: Filing & Accounts Dates ── */}
                <VuiBox mb={1.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                  <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1 }}>
                    📅 Accounts &amp; Filing Dates
                  </VuiTypography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: "Accounting Ref Date",    value: chData.company.accountingReferenceDate },
                      { label: "Next Accounts Due",      value: chData.company.nextAccountsDue },
                      { label: "Accounts Period End",    value: chData.company.nextAccountsPeriodEndOn },
                      { label: "Last Accounts Made Up",  value: chData.company.lastAccountsMadeUpTo },
                      { label: "Last Accounts Type",     value: chData.company.lastAccountsType },
                      { label: "Conf. Statement Due",    value: chData.company.confirmationStatementNextDue },
                      { label: "Last Conf. Statement",   value: chData.company.confirmationStatementLastMadeUpTo },
                    ].filter(f => f.value).map(f => (
                      <Grid item xs={6} sm={4} key={f.label}>
                        <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  fontSize: "0.62rem", mb: 0.2, opacity: 0.7 }}>{f.label}</VuiTypography>
                        <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem" }}>{f.value}</VuiTypography>
                      </Grid>
                    ))}
                  </Grid>
                </VuiBox>

                {/* ── Section 4: SIC Codes ── */}
                {chData.company.sicCodes?.length > 0 && (
                  <VuiBox mb={1.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                    <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1 }}>
                      🏭 SIC Codes (Nature of Business)
                    </VuiTypography>
                    <VuiBox display="flex" gap={0.8} flexWrap="wrap">
                      {chData.company.sicCodes.map(code => (
                        <Chip key={code} label={code} size="small"
                          sx={{ bgcolor: "rgba(0,117,255,0.15)", color: TC.link, border: "1px solid rgba(0,117,255,0.3)", fontSize: "0.72rem", height: 22 }}
                        />
                      ))}
                    </VuiBox>
                  </VuiBox>
                )}

                {/* ── Section 5: Risk Flags ── */}
                {(chData.company.hasCharges || chData.company.hasInsolvencyHistory || chData.company.hasBeenLiquidated) && (
                  <VuiBox mb={1.5} p={1.5} sx={{ background: "rgba(244,67,54,0.08)", borderRadius: "8px", border: "1px solid rgba(244,67,54,0.35)" }}>
                    <VuiTypography variant="caption" fontWeight="bold" sx={{ textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.72rem", display: "block", mb: 1, color: "#f44336" }}>
                      ⚠ Risk &amp; Compliance Flags
                    </VuiTypography>
                    <VuiBox display="flex" gap={1} flexWrap="wrap">
                      {chData.company.hasCharges          && <Chip label="⚠ Has Charges"          size="small" sx={{ bgcolor: "rgba(255,152,0,0.25)",  color: "#ffb74d", border: "1px solid rgba(255,152,0,0.6)",  fontSize: "0.8rem", fontWeight: 600 }} />}
                      {chData.company.hasInsolvencyHistory && <Chip label="🚨 Insolvency History"  size="small" sx={{ bgcolor: "rgba(244,67,54,0.25)", color: "#ef5350", border: "1px solid rgba(244,67,54,0.6)", fontSize: "0.8rem", fontWeight: 600 }} />}
                      {chData.company.hasBeenLiquidated   && <Chip label="🚨 Has Been Liquidated"  size="small" sx={{ bgcolor: "rgba(244,67,54,0.25)", color: "#ef5350", border: "1px solid rgba(244,67,54,0.6)", fontSize: "0.8rem", fontWeight: 600 }} />}
                    </VuiBox>
                  </VuiBox>
                )}

                {/* ── Section 6: Directors & Officers ── */}
                {chData.directors?.length > 0 && (
                  <VuiBox mb={0.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                    <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem" }}>
                        👤 Directors &amp; Officers
                      </VuiTypography>
                      <VuiBox display="flex" gap={0.5}>
                        <Chip label={`${chData.directors.filter(d => !d.resignedOn).length} active`} size="small"
                          sx={{ bgcolor: "rgba(76,175,80,0.15)", color: "#4caf50", border: "1px solid rgba(76,175,80,0.3)", fontSize: "0.62rem", height: 18 }} />
                        {chData.directors.filter(d => d.resignedOn).length > 0 && (
                          <Chip label={`${chData.directors.filter(d => d.resignedOn).length} resigned`} size="small"
                            sx={{ bgcolor: "#7a94b0", color: TC.secondary, border: "1px solid #d8e6f7", fontSize: "0.62rem", height: 18 }} />
                        )}
                      </VuiBox>
                    </VuiBox>
                    <VuiBox sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                      {chData.directors.map((d, i) => (
                        <VuiBox key={i} p={1} sx={{
                          background: d.resignedOn ? "rgba(255,255,255,0.02)" : "rgba(0,117,255,0.05)",
                          borderRadius: "6px",
                          border: `1px solid ${d.resignedOn ? "rgba(255,255,255,0.06)" : "rgba(0,117,255,0.15)"}`,
                          opacity: d.resignedOn ? 0.55 : 1,
                        }}>
                          <VuiBox display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                            <VuiBox>
                              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: 600 }} display="block">
                                {d.fullName}
                              </VuiTypography>
                              <VuiBox display="flex" gap={0.5} flexWrap="wrap" mt={0.3}>
                                <Chip label={d.role?.replace(/_/g, " ") || "Director"} size="small"
                                  sx={{ bgcolor: "rgba(0,117,255,0.15)", color: TC.link, fontSize: "0.6rem", height: 16 }} />
                                {d.nationality && <Chip label={d.nationality} size="small"
                                  sx={{ bgcolor: "#7a94b0", color: TC.midtone, fontSize: "0.6rem", height: 16 }} />}
                                {d.occupation && <Chip label={d.occupation} size="small"
                                  sx={{ bgcolor: "#7a94b0", color: TC.midtone, fontSize: "0.6rem", height: 16 }} />}
                              </VuiBox>
                            </VuiBox>
                            <VuiBox textAlign="right">
                              {d.appointedOn && (
                                <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  fontSize: "0.62rem" }}>
                                  Appointed {d.appointedOn}
                                </VuiTypography>
                              )}
                              {d.resignedOn && (
                                <VuiTypography variant="caption" sx={{ color: "#f44336", fontSize: "0.62rem" }} display="block">
                                  Resigned {d.resignedOn}
                                </VuiTypography>
                              )}
                              {d.dateOfBirth && (
                                <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  fontSize: "0.62rem", opacity: 0.6 }}>
                                  DOB: {d.dateOfBirth}
                                </VuiTypography>
                              )}
                            </VuiBox>
                          </VuiBox>
                          {d.serviceAddress?.postalCode && (
                            <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.6rem", opacity: 0.5, mt: 0.3, display: "block" }}>
                              📮 {[d.serviceAddress.addressLine1, d.serviceAddress.locality, d.serviceAddress.postalCode].filter(Boolean).join(", ")}
                            </VuiTypography>
                          )}
                        </VuiBox>
                      ))}
                    </VuiBox>
                  </VuiBox>
                )}
              </VuiBox>
            )}
          </VuiBox>
        )}

        <VuiBox component="form" id="client-profile-form" onSubmit={handleSubmit}>

          {/* ── Section 1: Company Profile ───────────────────────────── */}
          <SectionLabel>1 · Company Profile</SectionLabel>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="Legal Business Name *" value={form.legalName} onChange={set("legalName")} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="Trading Name (if different)" value={form.tradingName} onChange={set("tradingName")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput placeholder="Company Registration Number" value={form.companyRegNumber} onChange={set("companyRegNumber")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput placeholder="Incorporation Date (YYYY-MM-DD)" value={form.incorporationDate || ""} onChange={set("incorporationDate")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Status as reported by Companies House (e.g. active, dissolved)">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="CH Company Status" value={form.companyStatus} onChange={set("companyStatus")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledSelect value={form.jurisdiction} onChange={set("jurisdiction")}>
                {JURISDICTIONS.map((j) => <MenuItem key={j.value} value={j.value} sx={{ fontSize: "0.875rem" }}>{j.label}</MenuItem>)}
              </StyledSelect>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Raw company type from Companies House (e.g. ltd, llp, plc)">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="CH Company Type (raw)" value={form.companyTypeRaw} onChange={set("companyTypeRaw")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <VuiInput
                placeholder="Notes (internal)"
                value={form.companyNotes || ""}
                onChange={set("companyNotes")}
                multiline
                rows={3}
                sx={{ "& textarea": { color: "white !important" } }}
              />
            </Grid>
          </Grid>

          {/* ── Section 2: Registered Address ─────────────────────────── */}
          <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
          <SectionLabel>2 · Registered Address</SectionLabel>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <VuiInput
                placeholder="Address Line 1"
                value={form.registeredAddressObj?.addressLine1 || ""}
                onChange={setAddr("addressLine1")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput
                placeholder="Address Line 2"
                value={form.registeredAddressObj?.addressLine2 || ""}
                onChange={setAddr("addressLine2")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput
                placeholder="Town / City"
                value={form.registeredAddressObj?.locality || ""}
                onChange={setAddr("locality")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput
                placeholder="Region / County"
                value={form.registeredAddressObj?.region || ""}
                onChange={setAddr("region")}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <VuiInput
                placeholder="Postcode"
                value={form.registeredAddressObj?.postalCode || ""}
                onChange={setAddr("postalCode")}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <VuiInput
                placeholder="Country"
                value={form.registeredAddressObj?.country || ""}
                onChange={setAddr("country")}
              />
            </Grid>
            <Grid item xs={12}>
              <Tooltip title="Override: single-line address (used if structured address is not set)">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Flat address override (optional)" value={form.registeredAddress} onChange={set("registeredAddress")} />
                </span>
              </Tooltip>
            </Grid>
          </Grid>

          {/* ── Section 3: Companies House Filing & Dates ─────────────── */}
          <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
          <SectionLabel>3 · Companies House — Filing &amp; Dates</SectionLabel>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={4}>
              <Tooltip title="Accounting reference date in MM-DD format (e.g. 03-31 = 31 March)">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Accounting Ref Date (MM-DD)" value={form.accountingReferenceDate || ""} onChange={set("accountingReferenceDate")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Next accounts filing deadline from Companies House">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Next Accounts Due (YYYY-MM-DD)" value={form.nextAccountsDue || ""} onChange={set("nextAccountsDue")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="The period end date for the next accounts filing">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Accounts Period End On" value={form.nextAccountsPeriodEndOn || ""} onChange={set("nextAccountsPeriodEndOn")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Date last filed accounts were made up to">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Last Accounts Made Up To" value={form.lastAccountsMadeUpTo || ""} onChange={set("lastAccountsMadeUpTo")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Type of last accounts filed (e.g. total-exemption-full, full)">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Last Accounts Type" value={form.lastAccountsType || ""} onChange={set("lastAccountsType")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Next confirmation statement due date">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Conf. Statement Next Due" value={form.confirmationStatementNextDue || ""} onChange={set("confirmationStatementNextDue")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Date the last confirmation statement was made up to">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Last Conf. Statement Made Up To" value={form.confirmationStatementLastMadeUpTo || ""} onChange={set("confirmationStatementLastMadeUpTo")} />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={8}>
              <Tooltip title="SIC codes from Companies House — comma separated (e.g. 62012, 62020)">
                <span style={{ display: "block" }}>
                  <VuiInput
                    placeholder="SIC Codes (comma separated)"
                    value={Array.isArray(form.sicCodes) ? form.sicCodes.join(", ") : (form.sicCodes || "")}
                    onChange={(e) => setForm((p) => ({ ...p, sicCodes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))}
                  />
                </span>
              </Tooltip>
            </Grid>

            {/* Risk flags row */}
            <Grid item xs={12}>
              <VuiTypography variant="caption" fontWeight="bold" display="block" mb={1} sx={{ fontSize: "0.8rem", color: "#f44336", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                ⚠ Risk &amp; Compliance Flags (from Companies House)
              </VuiTypography>
              <VuiBox display="flex" gap={1.5} flexWrap="wrap">
                {[
                  { key: "hasCharges",          label: "⚠ Has Charges",          activeColor: "#ffb74d", activeBg: "rgba(255,152,0,0.25)",  inactiveColor: "#aac8f0" },
                  { key: "hasInsolvencyHistory", label: "🚨 Insolvency History",  activeColor: "#ef5350", activeBg: "rgba(244,67,54,0.25)", inactiveColor: "#aac8f0" },
                  { key: "hasBeenLiquidated",   label: "🚨 Has Been Liquidated",  activeColor: "#ef5350", activeBg: "rgba(244,67,54,0.25)", inactiveColor: "#aac8f0" },
                  { key: "canFile",             label: "✓ Can File",              activeColor: "#66bb6a", activeBg: "rgba(76,175,80,0.25)",  inactiveColor: "#aac8f0" },
                ].map(({ key, label, activeColor, activeBg, inactiveColor }) => (
                  <VuiBox
                    key={key}
                    onClick={() => setForm((p) => ({ ...p, [key]: !p[key] }))}
                    sx={{
                      cursor: "pointer",
                      px: 1.5, py: 0.7,
                      borderRadius: "6px",
                      border: `1px solid ${form[key] ? activeColor : "rgba(255,255,255,0.25)"}`,
                      background: form[key] ? activeBg : "rgba(255,255,255,0.06)",
                      transition: "all 0.15s",
                      userSelect: "none",
                    }}
                  >
                    <VuiTypography variant="caption" sx={{ color: form[key] ? activeColor : inactiveColor, fontSize: "0.8rem", fontWeight: form[key] ? 700 : 500 }}>
                      {label}
                    </VuiTypography>
                  </VuiBox>
                ))}
              </VuiBox>
            </Grid>
          </Grid>

          {/* ── Section 4: Tax Identity ────────────────────────────────── */}
          <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
          <SectionLabel>4 · Tax Identity</SectionLabel>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={4}>
              <StyledSelect value={String(form.vatRegistered)} onChange={(e) => set("vatRegistered")({ target: { value: e.target.value === "true" } })}>
                <MenuItem value="true"  sx={{ fontSize: "0.875rem" }}>VAT Registered</MenuItem>
                <MenuItem value="false" sx={{ fontSize: "0.875rem" }}>Not VAT Registered</MenuItem>
              </StyledSelect>
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput placeholder="VAT Number" value={form.vatNumber} onChange={set("vatNumber")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledSelect value={form.vatScheme} onChange={set("vatScheme")}>
                {VAT_SCHEMES.map((v) => <MenuItem key={v.value} value={v.value} sx={{ fontSize: "0.875rem" }}>{v.label}</MenuItem>)}
              </StyledSelect>
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput placeholder="UTR Number" value={form.utrNumber || ""} onChange={set("utrNumber")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput placeholder="Corporation Tax Number" value={form.corporationTaxNumber || ""} onChange={set("corporationTaxNumber")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput placeholder="PAYE Reference" value={form.payeReference || ""} onChange={set("payeReference")} />
            </Grid>
          </Grid>

          {/* ── Section 5: Financial Context ──────────────────────────── */}
          <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
          <SectionLabel>5 · Financial Context</SectionLabel>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={4}>
              <StyledSelect value={form.baseCurrency} onChange={set("baseCurrency")}>
                {CURRENCIES.map((c) => <MenuItem key={c.value} value={c.value} sx={{ fontSize: "0.875rem" }}>{c.label}</MenuItem>)}
              </StyledSelect>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledSelect value={form.accountingBasis} onChange={set("accountingBasis")}>
                {ACCOUNTING_BASIS.map((a) => <MenuItem key={a.value} value={a.value} sx={{ fontSize: "0.875rem" }}>{a.label}</MenuItem>)}
              </StyledSelect>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Format MM-DD — auto-derived from CH accounting reference date. E.g. 04-06 = 6 April">
                <span style={{ display: "block" }}>
                  <VuiInput placeholder="Financial Year Start (MM-DD)" value={form.financialYearStart} onChange={set("financialYearStart")} />
                </span>
              </Tooltip>
            </Grid>
          </Grid>

          {/* ── Section 6: Client Contact ──────────────────────────────── */}
          <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
          <SectionLabel>6 · Client Contact</SectionLabel>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="Contact Name" value={form.contactName} onChange={set("contactName")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="Contact Email" value={form.contactEmail} onChange={set("contactEmail")} type="email" />
            </Grid>
          </Grid>

          {/* ── Section 7: Accountant Relationship ────────────────────── */}
          <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />
          <SectionLabel>7 · Accountant Relationship</SectionLabel>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={4}>
              <StyledSelect value={form.accessLevel} onChange={set("accessLevel")}>
                {ACCESS_LEVELS.map((a) => <MenuItem key={a.value} value={a.value} sx={{ fontSize: "0.875rem" }}>{a.label}</MenuItem>)}
              </StyledSelect>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledSelect value={form.onboardingStatus} onChange={set("onboardingStatus")}>
                {ONBOARDING_STATUS.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>)}
              </StyledSelect>
            </Grid>
            <Grid item xs={12} md={4}>
              <VuiInput placeholder="Assigned Accountant UID (optional)" value={form.assignedAccountantUid} onChange={set("assignedAccountantUid")} />
            </Grid>
          </Grid>
        </VuiBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={() => guardClose(onClose)} disabled={isSubmitting}>Cancel</VuiButton>
        <VuiButton color="info" size="small" type="submit" form="client-profile-form" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : isEdit ? "Save Changes" : "Create Profile"}
        </VuiButton>
      </DialogActions>
    </Dialog>
    {ConfirmDialog}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function ClientProfiles() {
  const { tenantId, isSuperAdmin, isTenantAdmin, hasAllPermissions } = useAuthz();
  const { pinnedClientId, pinClient, clearPin } = useClient();
  const history = useHistory();
  const location = useLocation();
  const canManage = isSuperAdmin || isTenantAdmin || hasAllPermissions([PERMISSIONS.MANAGE_CLIENT_PROFILES]);
  const canArchive = isTenantAdmin;

  const [profiles, setProfiles]         = useState([]);
  const [isLoading, setIsLoading]       = useState(true);
  const [loadError, setLoadError]       = useState("");
  const [clientTypeTab, setClientTypeTab] = useState(0); // 0 = companies, 1 = individuals
  const [provisioningDirectors, setProvisioningDirectors] = useState(false);
  const [provisionMsg, setProvisionMsg] = useState("");
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [formOpen, setFormOpen]         = useState(false);
  const [createFormType, setCreateFormType] = useState("company"); // "company" | "individual"
  const [editProfile, setEditProfile]   = useState(null);
  const [viewProfile, setViewProfile]   = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [isArchiving, setIsArchiving]   = useState(false);
  const [successMsg, setSuccessMsg]     = useState("");
  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), 4000); };
  const [userMap, setUserMap]           = useState({});   // uid → displayName

  // Directors tab state
  const [directors, setDirectors]           = useState([]);
  const [directorsLoading, setDirectorsLoading] = useState(false);
  const [directorsError, setDirectorsError]     = useState("");
  const [dirSearch, setDirSearch]           = useState("");
  const [dirStatusFilter, setDirStatusFilter] = useState("active");

  // Load tenant users once so UIDs can be resolved to names
  useEffect(() => {
    if (!tenantId) return;
    listTenantUsers({ tenantId })
      .then(({ users }) => {
        const map = {};
        users.forEach((u) => { if (u.uid) map[u.uid] = u.displayName || u.email || u.uid; });
        setUserMap(map);
      })
      .catch(() => {});
  }, [tenantId]);

  // Auto-open create dialog from ?create=company or ?create=individual
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const createType = params.get("create");
    if (createType === "company" || createType === "individual") {
      setEditProfile(null);
      setCreateFormType(createType);
      setFormOpen(true);
      history.replace({ pathname: location.pathname });
    }
  }, [location.search]); // eslint-disable-line

  const load = async () => {
    if (!tenantId) return;
    setIsLoading(true);
    setLoadError("");
    try {
      const { clientProfiles } = await listClientProfiles({ tenantId, status: statusFilter || undefined });
      setProfiles(clientProfiles);
    } catch (err) {
      setLoadError(err?.message || "Failed to load client profiles.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId, statusFilter]);

  useEffect(() => {
    if (!tenantId) return;
    setDirectorsLoading(true);
    setDirectorsError("");
    listAllDirectorsForTenant(tenantId)
      .then(({ directors: data }) => setDirectors(data))
      .catch((e) => setDirectorsError(e?.message || "Failed to load directors."))
      .finally(() => setDirectorsLoading(false));
  }, [tenantId]);

  const filtered = useMemo(() => {
    let byType;
    if (clientTypeTab === 0)      byType = profiles.filter((p) => p.clientType !== "individual");
    else if (clientTypeTab === 2) byType = profiles.filter((p) => p.clientType === "individual" && !p.linkedCompanyClientId);
    else                          byType = [];
    const q = search.trim().toLowerCase();
    if (!q) return byType;
    return byType.filter((p) =>
      (p.legalName        || "").toLowerCase().includes(q) ||
      (p.companyRegNumber || "").toLowerCase().includes(q) ||
      (p.contactEmail     || "").toLowerCase().includes(q) ||
      (p.vatNumber        || "").toLowerCase().includes(q) ||
      (p.jurisdiction     || "").toLowerCase().includes(q)
    );
  }, [profiles, search, clientTypeTab]);

  const filteredDirectors = useMemo(() => {
    // Exclude directors whose parent company is archived
    const activeCompanyIds = new Set(
      profiles.filter((p) => p.clientType !== "individual" && p.status !== "archived").map((p) => p.id)
    );
    let list = directors.filter((d) => activeCompanyIds.has(d.clientId));
    if (dirStatusFilter === "active")   list = list.filter((d) => !d.resignedOn);
    if (dirStatusFilter === "resigned") list = list.filter((d) =>  d.resignedOn);
    const q = dirSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((d) =>
      (d.fullName   || "").toLowerCase().includes(q) ||
      (d.clientName || "").toLowerCase().includes(q) ||
      (d.role       || "").toLowerCase().includes(q)
    );
  }, [directors, profiles, dirStatusFilter, dirSearch]);

  const activeDirectorCount = useMemo(() => {
    const activeCompanyIds = new Set(
      profiles.filter((p) => p.clientType !== "individual" && p.status !== "archived").map((p) => p.id)
    );
    return directors.filter((d) => activeCompanyIds.has(d.clientId) && !d.resignedOn).length;
  }, [directors, profiles]);

  const handleProvisionDirectors = async () => {
    setProvisioningDirectors(true);
    setProvisionMsg("");
    try {
      const result = await provisionDirectorProfiles(tenantId);
      const n = result?.totalProvisioned || 0;
      setProvisionMsg(n > 0 ? `${n} individual profile(s) created from directors.` : "All directors already have profiles.");
      load(); // refresh the list
    } catch (err) {
      setProvisionMsg(err?.message || "Failed to provision profiles.");
    } finally {
      setProvisioningDirectors(false);
    }
  };

  const handleSaved = (profile) => {
    setFormOpen(false); setEditProfile(null);
    flash(`"${profile.legalName}" saved.`);
    load();
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    setIsArchiving(true);
    try {
      await archiveClientProfile(tenantId, archiveTarget.id);
      flash(`"${archiveTarget.legalName}" archived.`);
      setArchiveTarget(null);
      load();
    } catch (err) {
      flash(`Error: ${err?.message || "Failed to archive."}`);
      setArchiveTarget(null);
    } finally {
      setIsArchiving(false);
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout><DashboardNavbar />
        <VuiBox py={3}><VuiTypography color="error" variant="button">You do not have permission to manage client profiles.</VuiTypography></VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!tenantId) {
    return (
      <DashboardLayout><DashboardNavbar />
        <VuiBox py={3}><VuiTypography color="warning" variant="button">No organisation selected. Please select an organisation first.</VuiTypography></VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3} sx={{ ml: 0, pl: 0, width: "100%", maxWidth: "100%" }}>

        {/* Page header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>Client Profiles</VuiTypography>
            <VuiTypography variant="caption" display="block" mt={0.5} sx={{ color: TC.secondary }}>
              Legal entities managed by your firm — each profile owns all consent, bank feeds, and financial data.
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" gap={1} flexWrap="wrap">
            <>
                <VuiButton
                    color="info" size="small"
                    startIcon={<BusinessIcon />}
                    onClick={() => { setEditProfile(null); setCreateFormType("company"); setFormOpen(true); }}
                    sx={{
                      transition: "transform 150ms ease, box-shadow 150ms ease",
                      "&:hover": { transform: "translateY(-3px) scale(1.02)", boxShadow: "0 8px 20px rgba(10,22,40,0.08)" },
                    }}
                  >
                    New Company
                  </VuiButton>
                  <VuiButton
                    color="success" size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={() => { setEditProfile(null); setCreateFormType("individual"); setFormOpen(true); }}
                    sx={{
                      transition: "transform 150ms ease, box-shadow 150ms ease",
                      "&:hover": { transform: "translateY(-3px) scale(1.02)", boxShadow: "0 8px 20px rgba(10,22,40,0.08)" },
                    }}
                  >
                    New Individual
                  </VuiButton>
                </>
          </VuiBox>
        </VuiBox>

        {successMsg && <VuiTypography color="success" variant="button" mb={2} display="block">{successMsg}</VuiTypography>}
        {loadError  && <VuiTypography color="error"   variant="button" mb={2} display="block">{loadError}</VuiTypography>}

        {/* Pinned client banner */}
        {pinnedClientId && (() => {
          const pinned = profiles.find(p => p.id === pinnedClientId);
          return (
            <VuiBox mb={2} px={2} py={1} borderRadius="8px"
              display="flex" alignItems="center" justifyContent="space-between"
              sx={{ background: "rgba(255,179,0,0.1)", border: "1px solid rgba(255,179,0,0.35)" }}>
              <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                📌 Active client: <strong>{pinned?.legalName || pinnedClientId}</strong>
                {pinned?.companyRegNumber && <span style={{ opacity: 0.6 }}> · {pinned.companyRegNumber}</span>}
                <span style={{ opacity: 0.5, marginLeft: 8 }}>— Directors page will filter to this company</span>
              </VuiTypography>
              <VuiButton size="small" variant="outlined" color="text"
                sx={{ fontSize: "0.7rem", py: 0.3, px: 1, borderColor: "rgba(255,179,0,0.4)", color: "#ffb300" }}
                onClick={clearPin}>
                ✕ Unpin
              </VuiButton>
            </VuiBox>
          );
        })()}

        {/* Toolbar */}
        <Card sx={{ mb: 2 }}>
          <VuiBox px={2} pt={1.5} pb={0}>
            <Tabs
              value={clientTypeTab}
              onChange={(_, v) => setClientTypeTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 32,
                "& .MuiTabs-indicator": { backgroundColor: "#0075ff" },
                "& .MuiTab-root": { color: TC.secondary, fontSize: "0.8rem", minHeight: 32, py: 0.5, textTransform: "none", lineHeight: 1 },
                "& .Mui-selected": { color: "#0a1628 !important", fontWeight: "700 !important" },
                "& .MuiTabScrollButton-root": { color: TC.secondary, opacity: 1 },
              }}
            >
              {[
                { emoji: "🏢", text: `Companies (${profiles.filter(p => p.clientType !== "individual").length})` },
                { emoji: "🏛", text: `Directors (${activeDirectorCount})` },
                { emoji: "👤", text: `Individuals (${profiles.filter(p => p.clientType === "individual" && !p.linkedCompanyClientId).length})` },
              ].map(({ emoji, text }) => (
                <Tab key={text} label={
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ display: "inline-block", verticalAlign: "middle", position: "relative", top: "-1px", fontSize: "0.9rem" }}>{emoji}</span>
                    <span style={{ verticalAlign: "middle" }}>{text}</span>
                  </span>
                } />
              ))}
            </Tabs>
          </VuiBox>
          {clientTypeTab !== 1 && (
          <VuiBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <VuiBox sx={{ flex: 1, minWidth: 220, maxWidth: 380 }}>
              <VuiInput
                placeholder="Search name, reg, VAT, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                inputProps={{ "aria-label": "Search client profiles" }}
                icon={{ component: <SearchIcon />, direction: "left" }}
              />
            </VuiBox>
            <VuiBox sx={{ minWidth: 160 }}>
              <StyledSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value=""         sx={{ fontSize: "0.875rem", color: TC.midtone }}>All Statuses</MenuItem>
                <MenuItem value="active"    sx={{ fontSize: "0.875rem" }}>Active</MenuItem>
                <MenuItem value="suspended" sx={{ fontSize: "0.875rem" }}>Suspended</MenuItem>
                <MenuItem value="archived"  sx={{ fontSize: "0.875rem" }}>Archived</MenuItem>
                <MenuItem value="dissolved" sx={{ fontSize: "0.875rem" }}>Dissolved</MenuItem>
              </StyledSelect>
            </VuiBox>
            <VuiTypography variant="caption" sx={{ color: TC.secondary,  ml: "auto" }}>
              {filtered.length} profile{filtered.length !== 1 ? "s" : ""}
            </VuiTypography>
          </VuiBox>
          )}
        </Card>

        {/* Directors tab */}
        {clientTypeTab === 1 && (
          <>
            <Card sx={{ mb: 2 }}>
              <VuiBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <VuiBox sx={{ flex: 1, minWidth: 220, maxWidth: 380 }}>
                  <VuiInput
                    placeholder="Search name, company, role…"
                    value={dirSearch}
                    onChange={(e) => setDirSearch(e.target.value)}
                    icon={{ component: <SearchIcon />, direction: "left" }}
                  />
                </VuiBox>
                <VuiBox sx={{ minWidth: 160 }}>
                  <StyledSelect value={dirStatusFilter} onChange={(e) => setDirStatusFilter(e.target.value)}>
                    <MenuItem value=""         sx={{ fontSize: "0.875rem", color: TC.midtone }}>All Status</MenuItem>
                    <MenuItem value="active"   sx={{ fontSize: "0.875rem" }}>Active</MenuItem>
                    <MenuItem value="resigned" sx={{ fontSize: "0.875rem" }}>Resigned</MenuItem>
                  </StyledSelect>
                </VuiBox>
                <VuiTypography variant="caption" sx={{ color: TC.secondary,  ml: "auto" }}>
                  {filteredDirectors.length} director{filteredDirectors.length !== 1 ? "s" : ""}
                </VuiTypography>
              </VuiBox>
            </Card>
            <Card>
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="22px">
                <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>Directors</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.72rem", opacity: 0.7 }}>
                  Populated automatically via Companies House sync
                </VuiTypography>
              </VuiBox>
              {directorsLoading ? (
                <LoadingSpinner label="Loading directors…" />
              ) : directorsError ? (
                <VuiTypography color="error" variant="button" display="block">{directorsError}</VuiTypography>
              ) : filteredDirectors.length === 0 ? (
                <VuiBox py={4} textAlign="center">
                  <AccountBalanceIcon sx={{ fontSize: 48, color: TC.tertiary, mb: 1 }} />
                  <VuiTypography variant="button" display="block" sx={{ color: TC.primary }}>
                    {directors.length === 0
                      ? "No directors yet. Sync a company from Companies House to populate this list."
                      : "No directors match your search."}
                  </VuiTypography>
                </VuiBox>
              ) : (
                <VuiBox>
                  <VuiBox display="grid" sx={{ gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr", gap: 1, px: 1, pb: 0.5, borderBottom: "1px solid #e4eef8" }}>
                    {["Director", "Company", "Role", "Appointed", "Status"].map((h) => (
                      <VuiTypography key={h} variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.62rem", opacity: 0.6, fontWeight: 700, textTransform: "uppercase" }}>{h}</VuiTypography>
                    ))}
                  </VuiBox>
                  {filteredDirectors.map((d) => (
                    <VuiBox
                      key={`${d.clientId}-${d.id}`}
                      display="grid"
                      onClick={() => {
                        if (d.linkedClientId) {
                          history.push(`/individual-clients/${d.linkedClientId}`);
                        } else {
                          history.push(`/directors?clientId=${d.clientId}`);
                        }
                      }}
                      sx={{
                        gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr",
                        gap: 1, px: 1, py: 1,
                        cursor: "pointer",
                        borderBottom: "1px solid #e4eef8",
                        borderRadius: "6px",
                        transition: "background 0.15s",
                        "&:hover": { background: "rgba(0,117,255,0.08)" },
                        alignItems: "center",
                      }}
                    >
                      <VuiBox>
                        <VuiTypography variant="button" fontWeight="medium" display="block" sx={{ color: TC.primary }}>{d.fullName}</VuiTypography>
                        {d.dateOfBirth && <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.65rem" }} display="block">DOB: {d.dateOfBirth}</VuiTypography>}
                        {d.nationality && <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.65rem" }} display="block">{d.nationality}</VuiTypography>}
                      </VuiBox>
                      <VuiBox>
                        {(d.companies || [{ clientId: d.clientId, clientName: d.clientName, companyRegNumber: d.companyRegNumber, resignedOn: d.resignedOn }]).map((c, i) => (
                          <VuiBox key={i}>
                            <VuiTypography variant="caption" fontWeight="medium" display="block" sx={{ color: TC.primary }}>{c.clientName || c.clientId}</VuiTypography>
                            {c.companyRegNumber && <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Reg: {c.companyRegNumber}</VuiTypography>}
                          </VuiBox>
                        ))}
                      </VuiBox>
                      <VuiBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                        <Chip label={(d.role || "director").replace(/_/g, " ")} size="small"
                          sx={{ bgcolor: "rgba(0,117,255,0.15)", color: TC.link, fontSize: "0.65rem", height: 20 }} />
                        {d.source === "companies_house" && (
                          <Chip label="CH" size="small"
                            sx={{ bgcolor: "rgba(76,175,80,0.12)", color: "#4caf50", border: "1px solid rgba(76,175,80,0.3)", fontSize: "0.6rem", height: 18 }} />
                        )}
                      </VuiBox>
                      <VuiBox>
                        {d.appointedOn && <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>From: {d.appointedOn}</VuiTypography>}
                        {d.resignedOn  && <VuiTypography variant="caption" sx={{ color: "#f44336", fontSize: "0.65rem" }} display="block">Resigned: {d.resignedOn}</VuiTypography>}
                      </VuiBox>
                      <Chip
                        label={d.isActive === false || (!d.isActive && d.resignedOn) ? "Resigned" : "Active"}
                        size="small"
                        sx={{
                          bgcolor: (d.isActive === false || (!d.isActive && d.resignedOn)) ? "rgba(244,67,54,0.12)" : "rgba(76,175,80,0.12)",
                          color:   (d.isActive === false || (!d.isActive && d.resignedOn)) ? "#f44336" : "#4caf50",
                          border:  `1px solid ${(d.isActive === false || (!d.isActive && d.resignedOn)) ? "rgba(244,67,54,0.3)" : "rgba(76,175,80,0.3)"}`,
                          fontSize: "0.65rem", height: 20,
                        }}
                      />
                    </VuiBox>
                  ))}
                </VuiBox>
              )}
            </Card>
          </>
        )}

        {/* Client List — Companies (tab 0) and Individuals (tab 2) */}
        {clientTypeTab !== 1 && (
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="22px">
            <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>
              Client List
            </VuiTypography>
          </VuiBox>

          {isLoading ? (
            <SkeletonTable
              rows={6}
              columns={[
                { name: "client",    skeleton: "multi" },
                { name: "identity"  },
                { name: "contact"   },
                { name: "financial" },
                { name: "status",    skeleton: "badge", align: "center" },
                { name: "actions",   skeleton: "icon",  align: "center" },
              ]}
            />
          ) : filtered.length === 0 ? (
            <VuiBox py={4} textAlign="center">
              <AccountBalanceIcon sx={{ fontSize: 48, color: TC.tertiary, mb: 1 }} />
              <VuiTypography variant="button" display="block" mb={1.5} sx={{ color: TC.primary }}>
                {clientTypeTab === 2
                  ? "No individual clients yet. Use the New Individual button to add one."
                  : profiles.length === 0 ? "No client profiles yet. Create one to get started." : "No profiles match your search."}
              </VuiTypography>
            </VuiBox>
          ) : (
            <VuiBox
              sx={{
                // Allow horizontal scrolling on small screens so wide tables remain usable
                overflowX: { xs: "auto", sm: "visible" },
                WebkitOverflowScrolling: "touch",
                // Ensure the inner table can exceed the viewport width to trigger scrolling
                "& .MuiTable-root": { minWidth: 800 },
                "& th": {
                  borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                    `${borderWidth[1]} solid ${grey[700]}`,
                },
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                      `${borderWidth[1]} solid ${grey[700]}`,
                  },
                },
              }}
            >
              <Table
                columns={[
                  { name: "client",      align: "left"   },
                  { name: "identity",    align: "left"   },
                  { name: "contact",     align: "left"   },
                  { name: "financial",   align: "left"   },
                  { name: "status",      align: "center" },
                  { name: "actions",     align: "center" },
                ]}
                onRowClick={(row) => {
                  const p = filtered.find(f => f.id === row._id);
                  if (p?.clientType === "individual") {
                    history.push(`/individual-clients/${row._id}`, { profile: p });
                  } else {
                    history.push(`/client-profiles/${row._id}`, { profile: p });
                  }
                }}
                rows={filtered.map((p) => ({
                  hasBorder: true,
                  _id: p.id,

                  client: (
                    <VuiBox display="flex" flexDirection="column" px={1} py={0.5}>
                      <VuiBox display="flex" alignItems="center" gap={0.8}>
                        <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>
                          {p.legalName}
                        </VuiTypography>
                        {p.companyStatus && (
                          <Chip
                            label={p.companyStatus}
                            size="small"
                            sx={{
                              height: 16, fontSize: "0.58rem", fontWeight: 700,
                              bgcolor: p.companyStatus === "active" ? "rgba(76,175,80,0.15)" : "rgba(244,67,54,0.15)",
                              color:   p.companyStatus === "active" ? "#4caf50" : "#f44336",
                              border:  `1px solid ${p.companyStatus === "active" ? "rgba(76,175,80,0.4)" : "rgba(244,67,54,0.4)"}`,
                            }}
                          />
                        )}
                      </VuiBox>
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                        {labelFor(JURISDICTIONS, p.jurisdiction)} · {labelFor(BUSINESS_TYPES, p.businessType)}
                      </VuiTypography>
                      {p.nextAccountsDue && (
                        <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.62rem", opacity: 0.6 }}>
                          Accounts due: {p.nextAccountsDue}
                        </VuiTypography>
                      )}
                    </VuiBox>
                  ),

                  identity: (
                    <VuiBox display="flex" flexDirection="column">
                      {p.companyRegNumber && (
                        <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                          Reg: <span style={{ color: TC.primary }}>{p.companyRegNumber}</span>
                        </VuiTypography>
                      )}
                      {p.vatNumber && (
                        <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                          VAT: <span style={{ color: TC.primary }}>{p.vatNumber}</span>
                        </VuiTypography>
                      )}
                      {!p.companyRegNumber && !p.vatNumber && (
                        <VuiTypography variant="caption" sx={{ color: TC.secondary }}>—</VuiTypography>
                      )}
                    </VuiBox>
                  ),

                  contact: (
                    <VuiBox display="flex" flexDirection="column">
                      <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>
                        {p.contactName || "—"}
                      </VuiTypography>
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                        {p.contactEmail || ""}
                      </VuiTypography>
                    </VuiBox>
                  ),

                  financial: (
                    <VuiBox display="flex" flexDirection="column">
                      <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>
                        {p.baseCurrency} · {labelFor(VAT_SCHEMES, p.vatScheme)}
                      </VuiTypography>
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                        FY: {p.financialYearStart} · {labelFor(TAX_REGIMES, p.taxRegime)}
                      </VuiTypography>
                    </VuiBox>
                  ),

                  status: <StatusBadge status={p.status} />,

                  actions: (
                    <VuiBox display="flex" gap={0.8} justifyContent="center" onClick={(e) => e.stopPropagation()}>
                      <VuiButton
                        size="small"
                        variant={pinnedClientId === p.id ? "contained" : "outlined"}
                        onClick={() => pinnedClientId === p.id ? clearPin() : pinClient(p.id, p.legalName)}
                        sx={{
                          minWidth: 36, px: 0.8, py: 0.3, fontSize: "1rem",
                          color: pinnedClientId === p.id ? "#fff" : "rgba(255,255,255,0.35)",
                          borderColor: pinnedClientId === p.id ? "#ffb300" : "rgba(255,255,255,0.15)",
                          bgcolor: pinnedClientId === p.id ? "#ffb300 !important" : "transparent",
                          boxShadow: pinnedClientId === p.id ? "0 0 8px rgba(255,179,0,0.6)" : "none",
                          "&:hover": {
                            bgcolor: pinnedClientId === p.id ? "#ffa000 !important" : "rgba(255,179,0,0.12)",
                            borderColor: "#ffb300",
                          },
                        }}
                        title={pinnedClientId === p.id ? "Unpin client" : "Set as active client"}
                      >
                        📌
                      </VuiButton>
                      <VuiButton
                        color="warning" size="small" variant="outlined"
                        onClick={() => { setEditProfile(p); setFormOpen(true); }}
                        sx={{ minWidth: 44, px: 1, py: 0.3, fontSize: "0.7rem" }}
                      >
                        Edit
                      </VuiButton>
                      {canArchive && p.status !== "archived" && (
                        <VuiButton
                          color="error" size="small"
                          onClick={() => setArchiveTarget(p)}
                          sx={{ minWidth: 62, px: 1, py: 0.3, fontSize: "0.7rem" }}
                        >
                          Archive
                        </VuiButton>
                      )}
                    </VuiBox>
                  ),
                }))}
              />
            </VuiBox>
          )}
        </Card>
        )}
      </VuiBox>

      {/* Dialogs */}
      <ProfileFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditProfile(null); }}
        onSaved={handleSaved}
        tenantId={tenantId}
        existingProfile={editProfile}
        initialClientType={createFormType}
      />

      <ViewDetailDialog
        open={false}
        onClose={() => {}}
        profile={null}
        tenantId={tenantId}
        userMap={userMap}
      />

      <Dialog
        open={Boolean(archiveTarget)}
        onClose={() => !isArchiving && setArchiveTarget(null)}
        PaperProps={{ sx: { ...DIALOG_PAPER_SX, minWidth: 340 } }}
      >
        <DialogTitle sx={{ color: TC.primary, pb: 1 }}>Archive Client Profile</DialogTitle>
        <DialogContent>
          <VuiTypography variant="button" sx={{ color: TC.primary }}>
            Archive <strong style={{ color: TC.primary }}>{archiveTarget?.legalName}</strong>?{" "}
            The profile is preserved for audit. Existing consent and bank feed data is unaffected.
          </VuiTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <VuiButton color="text" size="small" onClick={() => setArchiveTarget(null)} disabled={isArchiving}>Cancel</VuiButton>
          <VuiButton color="error" size="small" onClick={handleArchiveConfirm} disabled={isArchiving}>
            {isArchiving ? "Archiving…" : "Archive"}
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Floating add buttons */}
      {canManage && (
        <VuiBox sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 99, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Tooltip title="New Company" placement="left">
            <Fab size="medium" sx={{ bgcolor: "#0075ff", color: "#fff", "&:hover": { bgcolor: "#0060d0" } }}
              onClick={() => { setEditProfile(null); setCreateFormType("company"); setFormOpen(true); }}>
              <BusinessIcon />
            </Fab>
          </Tooltip>
          <Tooltip title="New Individual" placement="left">
            <Fab size="medium" sx={{ bgcolor: "#4caf50", color: "#fff", "&:hover": { bgcolor: "#388e3c" } }}
              onClick={() => { setEditProfile(null); setCreateFormType("individual"); setFormOpen(true); }}>
              <PersonAddIcon />
            </Fab>
          </Tooltip>
        </VuiBox>
      )}
    </DashboardLayout>
  );
}

export default ClientProfiles;

